import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';

interface TierBonusSetting {
  id: number;
  tier: string;
  bonusType: string;
  enabled: boolean;
  percent: string;
  minAmount: number | null;
  maxAmount: number | null;
  rolloverMultiplier: string;
  periodType: string;
  resetDay: number | null;
  freeSpinCount: number;
  config: any;
}

interface EditBonusSettingModalProps {
  open: boolean;
  onClose: () => void;
  setting: TierBonusSetting | null;
  onSave: (payload: any) => Promise<void>;
  saving: boolean;
}

const EditBonusSettingModal: React.FC<EditBonusSettingModalProps> = ({
  open,
  onClose,
  setting,
  onSave,
  saving,
}) => {
  const [percent, setPercent] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [rolloverMultiplier, setRolloverMultiplier] = useState('');
  const [periodType, setPeriodType] = useState('');
  const [resetDay, setResetDay] = useState('');
  const [freeSpinCount, setFreeSpinCount] = useState('');

  useEffect(() => {
    if (setting) {
      setPercent(setting.percent && setting.percent !== '0' ? setting.percent : '');
      setMinAmount(setting.minAmount !== null && setting.minAmount !== undefined && setting.minAmount !== 0 ? String(setting.minAmount) : '');
      setMaxAmount(setting.maxAmount !== null && setting.maxAmount !== undefined && setting.maxAmount !== 0 ? String(setting.maxAmount) : '');
      setRolloverMultiplier(setting.rolloverMultiplier && setting.rolloverMultiplier !== '0' ? setting.rolloverMultiplier : '');
      setPeriodType(setting.periodType || 'none');
      setResetDay(setting.resetDay !== null && setting.resetDay !== undefined ? String(setting.resetDay) : '');
      setFreeSpinCount(setting.freeSpinCount ? String(setting.freeSpinCount) : '');
    }
  }, [setting]);

  const handleNumericChange = (setter: React.Dispatch<React.SetStateAction<string>>, allowDecimal = false) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = e.target.value;
    if (val === '') {
      setter('');
      return;
    }
    const regex = allowDecimal ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
    if (regex.test(val)) {
      setter(val);
    }
  };

  const handleSave = () => {
    const payload = {
      percent: percent === '' ? 0 : Number(percent),
      minAmount: minAmount === '' ? null : Number(minAmount),
      maxAmount: maxAmount === '' ? null : Number(maxAmount),
      rolloverMultiplier: rolloverMultiplier === '' ? 0 : Number(rolloverMultiplier),
      periodType,
      resetDay: resetDay === '' ? null : Number(resetDay),
      freeSpinCount: freeSpinCount === '' ? 0 : Number(freeSpinCount),
    };
    onSave(payload);
  };

  const formatBonusType = (type: string) => {
    if (!type) return '';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--bg-header)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)',
        },
      }}
      disableRestoreFocus
    >
      <DialogTitle sx={{ color: 'white', pb: 1 }}>
        Edit {setting ? `${formatBonusType(setting.bonusType)} (${setting.tier.toUpperCase()})` : 'Bonus Setting'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField
            fullWidth
            label="Percent (%)"
            variant="outlined"
            value={percent}
            onChange={handleNumericChange(setPercent, true)}
            placeholder="0"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />

          <TextField
            fullWidth
            label="Min Amount ($)"
            variant="outlined"
            value={minAmount}
            onChange={handleNumericChange(setMinAmount, true)}
            placeholder="No minimum limit"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />

          <TextField
            fullWidth
            label="Max Amount ($)"
            variant="outlined"
            value={maxAmount}
            onChange={handleNumericChange(setMaxAmount, true)}
            placeholder="No maximum limit"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />

          <TextField
            fullWidth
            label="Rollover Multiplier"
            variant="outlined"
            value={rolloverMultiplier}
            onChange={handleNumericChange(setRolloverMultiplier, true)}
            placeholder="0"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />

          <FormControl fullWidth sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
            }
          }}>
            <InputLabel sx={{ color: 'var(--text-secondary)' }}>Period Type</InputLabel>
            <Select
              value={periodType}
              label="Period Type"
              onChange={(e) => setPeriodType(e.target.value as string)}
              sx={{
                color: 'white',
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="none">NONE</MenuItem>
              <MenuItem value="weekly">WEEKLY</MenuItem>
              <MenuItem value="monthly">MONTHLY</MenuItem>
              <MenuItem value="pre_monthly">PRE MONTHLY</MenuItem>
              <MenuItem value="quarterly">QUARTERLY</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Reset Day"
            variant="outlined"
            value={resetDay}
            onChange={handleNumericChange(setResetDay, false)}
            placeholder="None"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />

          <TextField
            fullWidth
            label="Free Spin Count"
            variant="outlined"
            value={freeSpinCount}
            onChange={handleNumericChange(setFreeSpinCount, false)}
            placeholder="0"
            sx={{
              input: { color: 'white' },
              label: { color: 'var(--text-secondary)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: 'var(--accent-blue)',
            '&:hover': { backgroundColor: '#0092c7' },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBonusSettingModal;
